import { createFileRoute } from '@tanstack/react-router'
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  UserX,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { useLocations, useSetUserActive, useUsers } from '@/api/queries'
import type { UserItem } from '@/api/types'
import { ConfirmDialog } from '@/components/ui/dialog'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  cn,
} from '@/components/ui/primitives'
import { UserFormDialog, type UserDialogState } from '@/features/users/user-form'
import { UserLocationsDialog } from '@/features/users/user-locations'
import { formatDate } from '@/shared/format'
import { apiErrorLabel, roleLabel } from '@/shared/labels'
import { canManageUsers } from '@/shared/permissions'

const searchSchema = z.object({
  q: z.string().optional(),
  role: z.enum(['employee', 'manager', 'admin', 'auditor']).optional(),
  location_id: z.string().optional(),
  is_active: z.boolean().optional(),
  page: z.number().int().min(1).default(1),
})

export const Route = createFileRoute('/_auth/users/')({
  validateSearch: searchSchema,
  component: UsersPage,
})

const LIMIT = 25

function UsersPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { me } = Route.useRouteContext()
  const locations = useLocations()
  const setActive = useSetUserActive()

  const canManage = canManageUsers(me)
  const [dialog, setDialog] = useState<UserDialogState | null>(null)
  const [toggleTarget, setToggleTarget] = useState<UserItem | null>(null)
  const [locationsTarget, setLocationsTarget] = useState<UserItem | null>(null)

  const query = useUsers({
    q: search.q,
    role: search.role,
    location_id: search.location_id,
    is_active: search.is_active,
    limit: LIMIT,
    offset: (search.page - 1) * LIMIT,
  })

  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / LIMIT))

  const hasFilters =
    Boolean(search.q) ||
    Boolean(search.role) ||
    Boolean(search.location_id) ||
    search.is_active !== undefined

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Пользователи</h1>
          <p className="mt-0.5 text-[13px] text-ink-subtle">
            {total > 0 ? `Всего учётных записей: ${total}` : 'Доступ сотрудников к системе'}
          </p>
        </div>
        {canManage && (
          <Button variant="primary" onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="size-4" />
            Добавить пользователя
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-ink-subtle" />
          <Input
            value={search.q ?? ''}
            onChange={(event) =>
              void navigate({
                search: { ...search, q: event.target.value || undefined, page: 1 },
                replace: true,
              })
            }
            placeholder="Поиск по логину или ФИО"
            className="h-9 pl-9"
            aria-label="Поиск пользователей"
          />
        </div>

        <Select
          aria-label="Роль"
          value={search.role ?? ''}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                role: (event.target.value || undefined) as typeof search.role,
                page: 1,
              },
              replace: true,
            })
          }
          className="h-9 w-[190px]"
        >
          <option value="">Все роли</option>
          {(['employee', 'manager', 'admin', 'auditor'] as const).map((role) => (
            <option key={role} value={role}>
              {roleLabel(role)}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Точка"
          value={search.location_id ?? ''}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                location_id: event.target.value || undefined,
                page: 1,
              },
              replace: true,
            })
          }
          className="h-9 w-[210px]"
        >
          <option value="">Все точки</option>
          {locations.data?.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Статус"
          value={search.is_active === undefined ? '' : String(search.is_active)}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                is_active: event.target.value === '' ? undefined : event.target.value === 'true',
                page: 1,
              },
              replace: true,
            })
          }
          className="h-9 w-[180px]"
        >
          <option value="">Любой статус</option>
          <option value="true">Активные</option>
          <option value="false">Деактивированные</option>
        </Select>

        {hasFilters && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void navigate({ search: { page: 1 }, replace: true })}
          >
            <RotateCcw className="size-3.5" />
            Сбросить
          </Button>
        )}
      </div>

      <Card>
        {query.isError ? (
          <ErrorState
            message={apiErrorLabel(
              (query.error as { detail?: unknown }).detail,
              'Не удалось загрузить пользователей',
            )}
            onRetry={() => void query.refetch()}
          />
        ) : query.isLoading && !query.data ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-10" />
            ))}
          </div>
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Нет пользователей"
            description="Измените фильтры или добавьте нового."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[13px] text-ink-subtle">
                  {['Логин', 'ФИО', 'Роль', 'Точки', 'Добавлен', 'Статус'].map((title) => (
                    <th key={title} scope="col" className="px-5 py-2.5 font-medium">
                      {title}
                    </th>
                  ))}
                  {canManage && (
                    <th scope="col" className="px-5 py-2.5 text-right font-medium">
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {query.data?.items.map((user) => (
                  <tr
                    key={user.id}
                    className={cn(
                      'border-b border-line/60 transition-colors last:border-0 hover:bg-surface-muted/60',
                      !user.is_active && 'opacity-60',
                    )}
                  >
                    <td className="px-5 py-2.5 font-mono text-[13px] text-ink">{user.login}</td>
                    <td className="px-5 py-2.5 font-medium text-ink">
                      {user.full_name}
                      {user.id === me.id && (
                        <span className="ml-2 text-xs text-ink-subtle">это вы</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-[13px] text-ink-muted">
                      {roleLabel(user.role)}
                    </td>
                    <td className="px-5 py-2.5 text-[13px] text-ink-muted">
                      {user.locations.length === 0 ? (
                        '—'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {user.locations.map((location) => (
                            <Badge
                              key={location.id}
                              tone={location.id === user.location_id ? 'brand' : 'neutral'}
                            >
                              {location.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {user.role === 'manager' && user.locations.length === 0 && (
                        <span className="mt-1 block text-xs text-warning">
                          без точки не увидит разговоров
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-[13px] text-ink-muted">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-5 py-2.5">
                      {user.is_active ? (
                        <Badge tone="positive">активен</Badge>
                      ) : (
                        <Badge tone="neutral">деактивирован</Badge>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-5 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Точки"
                            onClick={() => setLocationsTarget(user)}
                          >
                            <MapPin className="size-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Изменить"
                            onClick={() => setDialog({ mode: 'edit', user })}
                          >
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={user.id === me.id && user.is_active}
                            title={
                              user.id === me.id && user.is_active
                                ? 'Нельзя деактивировать самого себя'
                                : user.is_active
                                  ? 'Деактивировать'
                                  : 'Восстановить'
                            }
                            onClick={() => setToggleTarget(user)}
                          >
                            {user.is_active ? (
                              <UserX className="size-3.5" />
                            ) : (
                              <RotateCcw className="size-3.5" />
                            )}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pageCount > 1 && (
          <footer className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
            <span className="text-[13px] text-ink-subtle">
              Страница {search.page} из {pageCount}
            </span>
            <Button
              size="sm"
              disabled={search.page <= 1}
              onClick={() =>
                void navigate({ search: { ...search, page: search.page - 1 }, replace: true })
              }
              title="Предыдущая страница"
            >
              <ChevronLeft className="size-3.5" />
            </Button>
            <Button
              size="sm"
              disabled={search.page >= pageCount}
              onClick={() =>
                void navigate({ search: { ...search, page: search.page + 1 }, replace: true })
              }
              title="Следующая страница"
            >
              <ChevronRight className="size-3.5" />
            </Button>
          </footer>
        )}
      </Card>

      <UserFormDialog
        state={dialog}
        onClose={() => setDialog(null)}
        locations={locations.data ?? []}
      />

      <UserLocationsDialog
        user={locationsTarget}
        locations={locations.data ?? []}
        onClose={() => setLocationsTarget(null)}
      />

      {/* В старой панели кнопка «Удалить» срабатывала молча — здесь подтверждение. */}
      <ConfirmDialog
        open={toggleTarget !== null}
        onOpenChange={(open) => !open && setToggleTarget(null)}
        title={
          toggleTarget?.is_active
            ? `Отключить доступ ${toggleTarget.full_name}?`
            : `Восстановить доступ ${toggleTarget?.full_name ?? ''}?`
        }
        description={
          toggleTarget?.is_active
            ? 'Пользователь не сможет войти ни в приложение на планшете, ни в веб-интерфейс. Его разговоры и метрики сохранятся.'
            : 'Пользователь снова сможет входить с прежним логином и паролем.'
        }
        confirmLabel={toggleTarget?.is_active ? 'Отключить' : 'Восстановить'}
        destructive={toggleTarget?.is_active ?? false}
        loading={setActive.isPending}
        onConfirm={() => {
          if (!toggleTarget) return
          setActive.mutate(
            { id: toggleTarget.id, active: !toggleTarget.is_active },
            {
              onSuccess: () => {
                toast.success(toggleTarget.is_active ? 'Доступ отключён' : 'Доступ восстановлен')
                setToggleTarget(null)
              },
              onError: (error) =>
                toast.error(apiErrorLabel((error as { detail?: unknown }).detail)),
            },
          )
        }}
      />
    </div>
  )
}
